import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Responsive Typography"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "responsive-typography"
    }}>{`Responsive Typography`}</h1>
    <p>{`While there are many different approaches to styling responsive typography on the web,
the `}<a parentName="p" {...{
        "href": "/theming/#styles"
      }}><inlineCode parentName="a">{`theme.styles`}</inlineCode></a>{` API can be used to create rich, responsive typography in MDX documents, with full control over the end result.
Used in combination with `}<a parentName="p" {...{
        "href": "/sx-prop/#responsive-values"
      }}>{`responsive array values`}</a>{` and `}<a parentName="p" {...{
        "href": "/guides/variants"
      }}>{`variants`}</a>{`, you can create reusable typographic styles that don't pollute the global CSS scope.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme with responsive typography styles
{
  fontSizes: [
    12, 14, 16, 18, 24, 32, 48, 64, 72,
  ],
  fonts: {
    body: 'system-ui, sans-serif',
    heading: 'Poppins, sans-serif',
  },
  fontWeights: {
    body: 400,
    heading: 900,
    bold: 700,
  },
  lineHeights: {
    body: 1.5,
    heading: 1.125,
  },
  text: {
    heading: {
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
    },
  },
  styles: {
    root: {
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 'body',
    },
    p: {
      fontSize: [2, 3],
    },
    h1: {
      variant: 'text.heading',
      fontSize: [5, 6, 7],
    },
    h2: {
      variant: 'text.heading',
      fontSize: [4, 5],
    },
  },
}
`}</code></pre>
    <h2 {...{
      "id": "caveats"
    }}>{`Caveats`}</h2>
    <p>{`Due to how CSS specificity works, if you've defined responsive styles in `}<inlineCode parentName="p">{`theme.styles`}</inlineCode>{`,
overriding styles with the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop requires also including styles for the `}<a parentName="p" {...{
        "href": "/theming/#breakpoints"
      }}><inlineCode parentName="a">{`breakpoints`}</inlineCode></a>{` set in the theme.
For example, with the following, the `}<inlineCode parentName="p">{`fontSize`}</inlineCode>{` style will only apply at the smallest breakpoint, and the `}<inlineCode parentName="p">{`theme.styles.h1`}</inlineCode>{` responsive styles will apply at other breakpoints.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme
{
  styles: {
    h1: {
      fontSize: [ 4, 5, 6 ],
    }
  }
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Themed.h1
  sx={{
    // styles for larger breakpoints will still apply
    // due to CSS specificity in media queries
    fontSize: 3,
  }}
/>
`}</code></pre>
    <h2 {...{
      "id": "non-mdx-content"
    }}>{`Non-MDX content`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "/themed"
      }}><inlineCode parentName="a">{`Themed`}</inlineCode></a>{` components can be used to pick up responsive styles outside of MDX,
but if you'd like to apply styles to other markdown or user generated content,
you can use the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop with a variant to style child elements.
What this does is take the entire `}<inlineCode parentName="p">{`theme.styles`}</inlineCode>{` object, adds a scoped classname to the `}<inlineCode parentName="p">{`<div>`}</inlineCode>{`,
and injects CSS with child selectors based on the keys in `}<inlineCode parentName="p">{`theme.styles`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`export default (props) => (
  <div
    sx={{
      variant: 'styles',
    }}>
    {props.children}
  </div>
)
`}</code></pre>
    <p>{`Optionally, the `}<inlineCode parentName="p">{`Themed.root`}</inlineCode>{` component can be used to set base styles within the `}<inlineCode parentName="p">{`<div>`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`export default (props) => (
  <div
    sx={{
      variant: 'styles',
    }}>
    <Themed.root>{props.children}</Themed.root>
  </div>
)
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      